import { EventEmitter } from 'events';

export default class MouseManager {
  pageX: number = 0;
  pageY: number = 0;
  pageDownX: number;
  pageDownY: number;
  pageUpX: number;
  pageUpY: number;
  moveX: number;
  moveY: number;
  timestampDown: number = 0;
  timestampUp: number = 0;
  downRect = [0, 0, 0, 0];
  button: number;

  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;

  listeners = {
    mousemove: [],
    mouseup: [],
    mousedown: [],
    mouseleave: []
  };

  constructor() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('mouseleave', this.handleMouseLeave);
    window.Mouse = this;
  }

  on(event: string, callback: (...args: any) => void, priority: Number) {
    const listeners: Function[] = this.listeners[event];
    callback.__priority = priority || 0;
    if (listeners.indexOf(callback) === -1) {
      listeners.push(callback);
    }

    listeners.sort((f1, f2) => f2.__priority - f1.__priority);
  }

  off(event: string, callback: (...args: any) => void) {
    const listeners: Function[] = this.listeners[event];
    if (!callback) {
      listeners.length = 0;
    } else {
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit = (event: string, e: MouseEvent) => {
    const listeners = this.listeners[event];
    for (let i = 0; i < listeners.length; i++) {
      if (listeners[i](e) === false) return;
    }
  };

  handleMouseLeave = (e: MouseEvent) => {
    this.emit('mouseleave', e);
    if (this.isMouseDown()) {
      this.handleMouseUp(e);
    }
  };

  handleMouseMove = (e: MouseEvent) => {
    this.moveX = e.pageX - this.pageX;
    this.moveY = e.pageY - this.pageY;
    this.pageX = e.pageX;
    this.pageY = e.pageY;
    this.altKey = e.altKey;
    this.metaKey = e.metaKey;
    this.shiftKey = e.shiftKey;
    this.emit('mousemove', e);
  };

  handleMouseDown = (e: MouseEvent) => {
    this.timestampDown = e.timeStamp;
    this.button = e.button;
    this.pageDownX = this.pageX = e.pageX;
    this.pageDownY = this.pageY = e.pageY;
    this.emit('mousedown', e);
  };

  handleMouseUp = (e: MouseEvent) => {
    this.timestampUp = e.timeStamp;
    this.pageUpX = this.pageX = e.pageX;
    this.pageUpY = this.pageY = e.pageY;
    this.emit('mouseup', e);
  };

  isMouseDown() {
    return this.timestampDown > this.timestampUp;
  }

  isMouseCenterDown() {
    return this.isMouseDown() && this.button === 1;
  }

  isMouseRightDown() {
    return this.isMouseDown() && this.button === 2;
  }

  isInside(e: MouseEvent, element: HTMLElement): boolean {
    let parent: HTMLElement = e.target;
    while (parent) {
      if (parent === element) return true;
      parent = parent.parentElement;
    }

    return false;
  }

  isInsideRole(e: MouseEvent, role: RegExp | String): boolean {
    let parent: HTMLElement = e.target;
    if (typeof role === 'string') role = new RegExp('^' + role + '$', 'i');
    while (parent) {
      if (role.test(parent.getAttribute('role'))) return true;
      parent = parent.parentElement;
    }

    return false;
  }

  getDownRect(quadraticSquare: boolean, scale: Number = 1): [number, number, number, number] {
    const { pageX, pageY, pageDownX, pageDownY } = this;
    const rect = this.downRect;
    rect[0] = Math.min(pageX, pageDownX);
    rect[1] = Math.min(pageY, pageDownY);
    rect[2] = (Math.max(pageX, pageDownX) - rect[0]) * scale;
    rect[3] = (Math.max(pageY, pageDownY) - rect[1]) * scale;
    if (quadraticSquare) {
      let newDim = Math.max(rect[2], rect[3]);
      if (pageX < pageDownX) rect[0] += rect[2] - newDim;
      if (pageY < pageDownY) rect[1] += rect[3] - newDim;
      rect[2] = rect[3] = newDim;
    }
    return rect;
  }

  mouseDownFor(moreThanMs: number) {
    return this.timestampUp - this.timestampDown > moreThanMs;
  }

  getDownDist() {
    return Math.sqrt(Math.pow(this.getDownDistX(), 2), Math.pow(this.getDownDistY(), 2));
  }

  getDownDistX() {
    return this.pageX - this.pageDownX;
  }

  getDownDistY() {
    return this.pageY - this.pageDownY;
  }
}
